import { useRef, useState } from "react";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { inventoryCapabilities } from "@/lib/inventory/capabilities";
import { listLocations, newInventoryKey, InventoryApiError, type InventoryProduct } from "@/lib/inventory/api";
import { createCount, getCount, listCounts, type CountDetail, type CreateCountInput } from "@/lib/inventory/countsApi";
import { useCountsText } from "@/lib/inventory/countsi18n";
import { InventoryCountReview } from "./InventoryCountReview";
import { countOwner, useCountLifetime } from "./countState";
import { ErrorNotice, Field, Panel, ProductPicker, selectClass, useRead, useInventoryWrite, ConfirmReview, Pager } from "./shared";

export function InventoryCounts({ initialCountId }: { initialCountId?: string | undefined } = {}) {
  const auth = useAuth();
  const t = useCountsText();
  const caps = inventoryCapabilities(auth.groups);
  if (!auth.isReady) return <p role="status">{t.loading}</p>;
  const owner = countOwner(auth);
  if (!owner || !caps.read) return <p role="alert">{t.denied}</p>;
  return <CountsWorkspace key={JSON.stringify([owner, initialCountId || "counts"])} initialCountId={initialCountId} />;
}
function CountsWorkspace({ initialCountId }: { initialCountId?: string | undefined }) {
  const t = useCountsText();
  const caps = inventoryCapabilities(useAuth().groups);
  const [selected, setSelected] = useState(initialCountId || "");
  const selection = useRef(0);
  const [selectionEpoch, setSelectionEpoch] = useState(0);
  function select(id: string) { setSelectionEpoch(++selection.current); setSelected(id); }
  const [openId, setOpenId] = useState("");
  const [offset, setOffset] = useState(0);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const listing = useRead(() => listCounts(25, offset), [offset]);
  const locations = useRead(listLocations, []);
  const locationSnapshot = useRef<Awaited<ReturnType<typeof listLocations>>["locations"] | null>(null);
  if (locations.data) locationSnapshot.current = locations.data.locations;
  const [recount, setRecount] = useState<CountDetail | undefined>(undefined);
  return <div className="space-y-5" data-testid="inventory-counts">
    <h2 className="text-xl">{t.title}</h2>
    {!caps.count && !caps.supervise && <p>{t.readOnly}</p>}
    <ErrorNotice error={listing.error || locations.error} />
    <Button variant="secondary" onClick={() => { setRefreshRevision(n => n + 1); listing.refresh(); locations.refresh(); }}>{t.refresh}</Button>
    {caps.count && locationSnapshot.current && <CreateCountForm key={`create-${recount?.count_id || "new"}`} {...(recount ? { recount } : {})} locations={locationSnapshot.current} disabled={locations.busy || listing.busy || !!locations.error || !!listing.error} selection={() => selection.current} onCreated={(d, generation) => { if (generation !== selection.current) return false; setSelected(d.count_id); listing.refresh(); return true; }} />}
    <Panel title={t.list}>
      <form className="flex flex-wrap gap-2" onSubmit={e => { e.preventDefault(); select(openId); }}>
        <Field label={t.countId}><Input value={openId} onChange={e => setOpenId(e.target.value)} pattern="CNT-[a-f0-9]{32}" required /></Field>
        <Button type="submit" disabled={!/^CNT-[a-f0-9]{32}$/.test(openId)}>{t.open}</Button>
      </form>
      {listing.busy && <p role="status">{t.loading}</p>}
      {listing.data?.counts.map(c => <div key={c.count_id} className="flex flex-wrap items-center gap-2 break-all"><Button variant="ghost" onClick={() => { select(c.count_id); }}>{c.count_id}</Button><span>{c.status} · {t.version}: {c.row_version}</span></div>)}
      {listing.data?.total === 0 && <p>{t.empty}</p>}
      <Pager offset={offset} total={listing.data?.total ?? 0} limit={25} onChange={setOffset} busy={listing.busy} />
    </Panel>
    {selected && <InventoryCountReview key={JSON.stringify([selected, selectionEpoch])} id={selected} revision={refreshRevision} locations={locations.data?.locations || []} onRecount={setRecount} onUpdated={listing.refresh} />}
  </div>;
}
function CreateCountForm({ locations, onCreated, recount, disabled, selection }: {
  locations: Awaited<ReturnType<typeof listLocations>>["locations"];
  onCreated: (d: CountDetail, generation: number) => boolean;
  selection: () => number;
  recount?: CountDetail;
  disabled: boolean;
}) {
  const t = useCountsText();
  const [ids, setIds] = useState<number[]>(recount?.scope.location_ids || []);
  const [product, setProduct] = useState<InventoryProduct | null>(null);
  const [all, setAll] = useState(!recount?.scope.product_id);
  const [kind, setKind] = useState<"FULL" | "CYCLE">(recount?.kind || "FULL");
  const [reason, setReason] = useState("");
  const active = useCountLifetime();
  const [pending, setPending] = useState<{ body: CreateCountInput; key: string; selection: number } | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const writer = useInventoryWrite();
  const locked = disabled || writer.busy || writer.blocked || refreshing;
  const attempted = useRef(false);
  const ref = useRef<string | null>(null);
  async function confirm() {
    if (!pending || locked) return;
    attempted.current = true;
    const result = await writer.run(pending.body, async () => {
      const r = await createCount(pending.body, pending.key);
      if (!active.current) return;
      ref.current = r.count_id;
      return getCount(r.count_id);
    });
    if (result && active.current) { setSent(onCreated(result, pending.selection) ? pending.selection : null); setPending(null); }
  }
  async function refresh() {
    setRefreshing(true);
    try {
      if (ref.current) { const d = await getCount(ref.current); if (!active.current) return; setSent(onCreated(d, pending?.selection ?? -1) ? pending!.selection : null); setPending(null); }
      else {
        await listCounts(1, 0);
        if (!active.current) return;
        if (writer.error instanceof InventoryApiError && [401, 403, 409, 422].includes(writer.error.status)) { setPending(null); attempted.current = false; }
      }
      if (active.current) writer.clear();
    }
    catch { /* Keep writes blocked until an actual successful refresh. */ }
    finally { if (active.current) setRefreshing(false); }
  }
  return <Panel title={recount ? t.recount : t.create}>
    {sent !== null && sent === selection() && <p role="status">{t.saved}</p>}
    <ErrorNotice error={writer.error} />
    {writer.error != null && <><p>{t.uncertain}</p><Button disabled={writer.busy || refreshing} onClick={() => void refresh()}>{t.refresh}</Button></>}
    {recount && <p>{t.recountOf}: {recount.count_id}</p>}
    <fieldset disabled={locked || !!pending} className="space-y-3">
      <legend>{t.locations}</legend>
      <div className="flex flex-wrap gap-3">{locations.map(l => <label key={l.location_id} className="flex min-h-10 items-center gap-2"><input type="checkbox" checked={ids.includes(l.location_id)} onChange={e => setIds(e.target.checked ? [...ids, l.location_id] : ids.filter(id => id !== l.location_id))} />{l.name}</label>)}</div>
      <label className="flex min-h-10 items-center gap-2"><input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} />{t.allProducts}</label>
      {!all && <><ProductPicker onSelect={setProduct} /><p>{t.selected}: {product?.product_id || recount?.scope.product_id || t.none} {product?.name}</p></>}
      <Field label={t.kind}><select className={selectClass} value={kind} onChange={e => setKind(e.target.value as "FULL" | "CYCLE")}><option value="FULL">{t.full}</option><option value="CYCLE">{t.cycle}</option></select></Field>
      <Field label={t.createReason}><Input value={reason} maxLength={400} onChange={e => setReason(e.target.value)} /></Field>
      <Button disabled={!ids.length || !reason.trim() || (!all && !product && !recount?.scope.product_id)} onClick={() => { setSent(null); attempted.current = false; ref.current = null; setPending({ key: newInventoryKey(), selection: selection(), body: { location_ids: ids, product_id: all ? 0 : (product?.product_id || recount!.scope.product_id), kind, reason, expected_version: 0, ...(recount ? { recount_of: recount.count_id } : {}) } }); }}>{t.reviewCreate}</Button>
    </fieldset>
    {pending && <ConfirmReview title={t.review} busy={writer.busy || refreshing} blocked={writer.blocked || disabled} onConfirm={() => void confirm()} onCancel={() => { if (!attempted.current) setPending(null); }}>
      <p>{t.locations}: {pending.body.location_ids.join(", ")} · {t.selected}: {pending.body.product_id === 0 ? t.allProducts : pending.body.product_id} · {pending.body.kind}</p>
      <p>{t.reason}: {pending.body.reason} · {t.version}: {pending.body.expected_version}</p><p>{t.frozen}</p>
      {writer.error != null && <p>{t.retry}</p>}
    </ConfirmReview>}
  </Panel>;
}
export default InventoryCounts;
